import { type ReactNode } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/auth.store'
import { useDashboardStats } from '@/features/dashboard/hooks/useDashboard'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'

interface NavItem {
  /** Clé i18n du libellé, résolue au rendu. */
  labelKey: string
  to: string
  icon: ReactNode
  /** Rôles autorisés ; absent, l'entrée est visible par tout le monde. */
  roles?: readonly string[]
}

const NAV_ITEMS: NavItem[] = [
  {
    labelKey: 'nav.dashboard',
    to: '/dashboard',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.chat',
    to: '/chat',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.courses',
    to: '/courses',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 14H7v-2h10v2zm0-4H7v-2h10v2zm0-4H7V6h10v2z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.games',
    to: '/jeux',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM6 15H5v1H3.5v-1H2.5v-1.5H3.5V13H5v1.5h1V15zm3.5-1.5h-2v-2h2v2zM17.5 15c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm3-3c-.83 0-1.5-.67-1.5-1.5S19.67 9 20.5 9s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.rewards',
    to: '/rewards',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.institution',
    to: '/institution',
    roles: ['school_admin'],
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M12 3 1 9l11 6 9-4.91V17h2V9L12 3zM5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.friends',
    to: '/friends',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
      </svg>
    ),
  },
  {
    labelKey: 'nav.profile',
    to: '/profile',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 shrink-0">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
    ),
  },
]

interface AppLayoutProps {
  readonly children: ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const location = useLocation()
  const { data: stats } = useDashboardStats()

  function handleLogout() {
    logout()
    navigate('/auth/login')
  }

  function isActive(to: string): boolean {
    return location.pathname === to || location.pathname.startsWith(to + '/')
  }

  const streakDays = stats?.streak_days ?? 0

  // L'espace établissement n'apparaît que pour ceux qui en administrent un.
  const navItems = NAV_ITEMS.filter(
    (item) => item.roles === undefined || item.roles.includes(user?.role ?? '')
  )

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 h-screen bg-white border-r border-outline-variant shrink-0">
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-5 py-4 shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
            <span className="text-white text-xs font-bold leading-none">M</span>
          </div>
          <span className="text-base font-bold text-primary tracking-tight font-display">
            {t('app.name')}
          </span>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-2 space-y-0.5" aria-label={t('nav.main')}>
          {navItems.map((item) => {
            const active = isActive(item.to)
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-label={t(item.labelKey)}
                aria-current={active ? 'page' : undefined}
                className={[
                  'relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-on-surface-variant hover:bg-surface-low',
                ].join(' ')}
              >
                {active && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full"
                    aria-hidden="true"
                  />
                )}
                {item.icon}
                {t(item.labelKey)}
              </Link>
            )
          })}
        </nav>

        {/* Streak widget */}
        <div className="mx-3 mb-3 px-4 py-3 rounded-xl bg-tertiary/10 flex items-center gap-2.5">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5 text-tertiary shrink-0"
            aria-hidden="true"
          >
            <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z" />
          </svg>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-tertiary uppercase tracking-wide leading-none mb-0.5">
              {t('nav.streak_label')}
            </p>
            <p className="text-sm font-bold text-on-surface">
              {t('nav.streak_days', { count: streakDays })}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="px-3 mb-3">
          <Link
            to="/chat"
            className="flex items-center justify-center w-full px-4 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            {t('nav.start_lesson')}
          </Link>
        </div>

        {/* User footer */}
        <div className="flex items-center gap-3 px-4 py-3 border-t border-outline-variant shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-white leading-none">
              {user?.first_name?.[0]?.toUpperCase() ?? 'U'}
            </span>
          </div>
          <p className="flex-1 text-sm font-medium text-on-surface truncate min-w-0">
            {user?.first_name} {user?.last_name}
          </p>
          <NotificationBell />
          <button
            type="button"
            onClick={handleLogout}
            aria-label={t('auth.logout')}
            title={t('auth.logout')}
            className="p-1.5 rounded-lg hover:bg-surface-low text-outline hover:text-on-surface transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
            </svg>
          </button>
        </div>
      </aside>

      {/* Main content column */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile top app bar */}
        <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-4 h-14 bg-surface/80 backdrop-blur-xl border-b border-outline-variant shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold leading-none">M</span>
            </div>
            <span className="text-sm font-bold text-primary font-display">{t('app.name')}</span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Link
              to="/profile"
              aria-label={t('nav.my_profile')}
              className="w-11 h-11 flex items-center justify-center shrink-0"
            >
              <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white leading-none">
                {user?.first_name?.[0]?.toUpperCase() ?? 'U'}
              </span>
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1 overflow-hidden pb-16 lg:pb-0">
          {children}
        </main>

        {/* Mobile bottom navigation */}
        <nav
          aria-label={t('nav.mobile')}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-20 flex items-center h-16 bg-surface/80 backdrop-blur-xl border-t border-outline-variant"
        >
          {navItems.map((item) => {
            const active = isActive(item.to)
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-label={t(item.labelKey)}
                aria-current={active ? 'page' : undefined}
                className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 h-full"
              >
                {active ? (
                  <span className="flex max-w-full items-center gap-1.5 rounded-full bg-secondary-fixed px-2 py-1 text-secondary">
                    {item.icon}
                    <span className="truncate text-[10px] font-semibold">{t(item.labelKey)}</span>
                  </span>
                ) : (
                  <>
                    <span className="text-outline">{item.icon}</span>
                    <span className="w-full truncate text-center text-[10px] font-medium text-outline">
                      {t(item.labelKey)}
                    </span>
                  </>
                )}
              </Link>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
